import type { KeyboardEvent, MouseEvent } from "react";
import { Avatar, Badge, Group, Mark, Stack, Text, UnstyledButton } from "@mantine/core";
import { createDurationMin, getPodcastImage, ImageSizes } from "@/core/helpers";
import { HighlightChunk } from "@/core/fuzzySearch";
import { RecentSearchEntry, SavedSearchEntry, formatRecentSearchTimestamp } from "@/stores/SearchStore";

interface PodcastSearchActionProps {
    podcast: Podcast;
    authorChunks?: HighlightChunk[];
    isBestMatch?: boolean;
    titleChunks?: HighlightChunk[];
}

interface EpisodeSearchActionProps {
    episode: Episode;
    isBestMatch?: boolean;
    subtitleChunks?: HighlightChunk[];
    titleChunks?: HighlightChunk[];
}

interface RecentSearchActionProps {
    onRemove: () => void;
    recentSearch: RecentSearchEntry;
}

interface SavedSearchActionProps {
    onRemove: () => void;
    savedSearch: SavedSearchEntry;
}

function HighlightedText({ chunks }: { chunks?: HighlightChunk[] }) {
    if (!chunks || chunks.length === 0) {
        return null;
    }

    return (
        <>
            {chunks.map((chunk, index) =>
                chunk.matched ? (
                    <Mark color="yellow.2" key={`${chunk.text}-${index}`} p={0}>
                        {chunk.text}
                    </Mark>
                ) : (
                    <span key={`${chunk.text}-${index}`}>{chunk.text}</span>
                )
            )}
        </>
    );
}

export function PodcastSearchAction({ podcast, authorChunks, isBestMatch, titleChunks }: PodcastSearchActionProps) {
    return (
        <Group gap="sm" wrap="nowrap">
            <Avatar radius="sm" size={40} src={getPodcastImage(podcast, ImageSizes.Thumb)}>
                {podcast.name.slice(0, 1)}
            </Avatar>
            <Stack gap={2} miw={0}>
                <Group gap={6} wrap="nowrap">
                    <Text fw={600} lineClamp={1}>
                        <HighlightedText chunks={titleChunks ?? [{ text: podcast.name, matched: false }]} />
                    </Text>
                    {isBestMatch && (
                        <Badge color="teal" size="xs" variant="light">
                            Bäst träff
                        </Badge>
                    )}
                </Group>
                <Text c="dimmed" size="sm" lineClamp={1}>
                    <HighlightedText
                        chunks={authorChunks ?? [{ text: podcast.author?.name ?? "Okänd upphovsman", matched: false }]}
                    />
                </Text>
            </Stack>
        </Group>
    );
}

export function EpisodeSearchAction({ episode, isBestMatch, subtitleChunks, titleChunks }: EpisodeSearchActionProps) {
    const podcast = episode.podcast;
    const minutes = createDurationMin(episode.length || episode.duration);

    return (
        <Group gap="sm" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" miw={0}>
                {podcast && (
                    <Avatar radius="sm" size={40} src={getPodcastImage(podcast, ImageSizes.Thumb)}>
                        {podcast.name.slice(0, 1)}
                    </Avatar>
                )}
                <Stack gap={2} miw={0}>
                    <Group gap={6} wrap="nowrap">
                        <Text fw={600} lineClamp={1}>
                            <HighlightedText chunks={titleChunks ?? [{ text: episode.title, matched: false }]} />
                        </Text>
                        {isBestMatch && (
                            <Badge color="teal" size="xs" variant="light">
                                Bäst träff
                            </Badge>
                        )}
                    </Group>
                    <Text c="dimmed" size="sm" lineClamp={1}>
                        <HighlightedText
                            chunks={subtitleChunks ?? [{ text: podcast?.name ?? "Okänd podcast", matched: false }]}
                        />
                    </Text>
                </Stack>
            </Group>
            <Text c="dimmed" size="xs">
                {minutes} min
            </Text>
        </Group>
    );
}

export function RecentSearchAction({ onRemove, recentSearch }: RecentSearchActionProps) {
    return (
        <Group gap="sm" justify="space-between" wrap="nowrap">
            <Stack gap={2} miw={0}>
                <Text fw={600} lineClamp={1}>
                    {recentSearch.query}
                </Text>
                <Group gap={8} wrap="nowrap">
                    <Text c="dimmed" size="sm">
                        {formatRecentSearchTimestamp(recentSearch.timestamp)}
                    </Text>
                    <Text c="dimmed" size="sm">
                        {recentSearch.resultCount} träffar
                    </Text>
                </Group>
            </Stack>
            <RemoveControl label="Ta bort senaste" onRemove={onRemove} />
        </Group>
    );
}

export function SavedSearchAction({ onRemove, savedSearch }: SavedSearchActionProps) {
    return (
        <Group gap="sm" justify="space-between" wrap="nowrap">
            <Stack gap={2} miw={0}>
                <Group gap={8} wrap="nowrap">
                    <Text fw={600} lineClamp={1}>
                        {savedSearch.query}
                    </Text>
                    <Badge color="blue" size="xs" variant="light">
                        Sparad
                    </Badge>
                </Group>
                <Text c="dimmed" size="sm">
                    Senast använd {formatRecentSearchTimestamp(savedSearch.lastUsedAt).toLocaleLowerCase()}
                </Text>
            </Stack>
            <RemoveControl label="Ta bort sparad" onRemove={onRemove} />
        </Group>
    );
}

function RemoveControl({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        // A span: the spotlight renders each action as a <button>, and a button inside a button is invalid HTML
        <UnstyledButton
            aria-label={label}
            component="span"
            onClick={(event: MouseEvent<HTMLElement>) => {
                event.preventDefault();
                event.stopPropagation();
                onRemove();
            }}
            onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onRemove();
                }
            }}
            role="button"
            tabIndex={0}
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            style={{
                color: "var(--mantine-color-dimmed)",
                cursor: "pointer",
                fontSize: "var(--mantine-font-size-xs)",
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}>
            Ta bort
        </UnstyledButton>
    );
}
